var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import React from "react";
import { Col, Row } from "@folio/stripes/components";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { FormattedMessage, useIntl } from "react-intl";
import css from "./reports.module.css";
import { createWipoReportSchema } from "../../../utils/validationUtils";
import { generateWipoReport } from "../../../api/reportsService";
import { WipoReportParamsRegistrationAgencyEnum, WipoReportDataRegistrationAgencyEnum, } from "../../../types/Api";
import { RecekTextField } from "../../inputs/RecekTextField";
import { RegistrationAgencySelect } from "./RegistrationAgencySelect";
import { ReportSubmitSection } from "./ReportSubmitSection";
import { useInlineReportMutation } from "../../../hooks/useInlineReportMutation";
var getDefaultValues = function () { return ({
    year: String(new Date().getFullYear() - 1),
    registrationAgency: WipoReportParamsRegistrationAgencyEnum.ISBN,
}); };
var transformFormToApiParams = function (data) { return ({
    year: parseInt(data.year, 10),
    registrationAgency: data.registrationAgency,
}); };
/**
 * Returns a translated label for the given registration agency enum value.
 */
var useAgencyLabel = function () {
    var formatMessage = useIntl().formatMessage;
    return function (agency) {
        var key = agency === WipoReportDataRegistrationAgencyEnum.ISBN
            ? "ui-br-agency.outputs.common.registrationAgency.isbn"
            : "ui-br-agency.outputs.common.registrationAgency.ismn";
        return formatMessage({ id: key });
    };
};
export var WipoReportForm = function () {
    var _a, _b, _c, _d, _e, _f, _g;
    var _h = useIntl(), formatMessage = _h.formatMessage, locale = _h.locale;
    var agencyLabel = useAgencyLabel();
    var _j = useForm({
        resolver: zodResolver(createWipoReportSchema(formatMessage)),
        defaultValues: getDefaultValues(),
    }), control = _j.control, handleSubmit = _j.handleSubmit;
    var _k = useInlineReportMutation({
        generateReport: function (_ky, data) {
            var apiParams = transformFormToApiParams(data);
            return generateWipoReport(_ky, apiParams);
        },
        successMessageId: "ui-br-agency.outputs.wipo-report.success.generate",
        errorMessageId: "ui-br-agency.outputs.wipo-report.error.generate",
    }), mutate = _k.mutate, isLoading = _k.isLoading, reportData = _k.data, successMessage = _k.successMessage, clearSuccessMessage = _k.clearSuccessMessage, errorModalOpen = _k.errorModalOpen, errorMessage = _k.errorMessage, closeErrorModal = _k.closeErrorModal;
    var formatCount = function (count) { return count.toLocaleString(locale); };
    return (React.createElement(React.Fragment, null,
        React.createElement("form", { onSubmit: handleSubmit(function (data) { return mutate(data); }) },
            React.createElement("h2", null,
                React.createElement(FormattedMessage, { id: "ui-br-agency.outputs.report.wipo-report" })),
            React.createElement("p", null,
                React.createElement(FormattedMessage, { id: "ui-br-agency.outputs.wipo-report.description" })),
            React.createElement(Row, null,
                React.createElement(Col, { xs: 12, md: 4 },
                    React.createElement(RegistrationAgencySelect, { control: control, name: "registrationAgency" })),
                React.createElement(Col, { xs: 12, md: 4 },
                    React.createElement(Controller, { control: control, name: "year", render: function (_a) {
                            var _b;
                            var field = _a.field, fieldState = _a.fieldState;
                            return (React.createElement(RecekTextField, __assign({}, field, { labelId: "ui-br-agency.outputs.wipo-report.field.year", error: (_b = fieldState.error) === null || _b === void 0 ? void 0 : _b.message, required: true })));
                        } }))),
            React.createElement(ReportSubmitSection, { isLoading: isLoading, generateButtonId: "ui-br-agency.outputs.wipo-report.button.generate", successMessage: successMessage, onSuccessDismiss: clearSuccessMessage, errorModalOpen: errorModalOpen, errorMessage: errorMessage, onErrorClose: closeErrorModal })),
        reportData &&
            (((_a = reportData.totalCount) !== null && _a !== void 0 ? _a : 0) === 0 &&
                ((_b = reportData.yearCount) !== null && _b !== void 0 ? _b : 0) === 0 ? (React.createElement("div", { className: css.wipoResult },
                React.createElement(FormattedMessage, { id: "ui-br-agency.outputs.wipo-report.result.empty" }))) : (React.createElement("div", { className: css.wipoResult },
                React.createElement("h3", null,
                    React.createElement(FormattedMessage, { id: "ui-br-agency.outputs.wipo-report.result.title", values: {
                            agency: agencyLabel((_c = reportData.registrationAgency) !== null && _c !== void 0 ? _c : WipoReportDataRegistrationAgencyEnum.ISBN),
                        } })),
                React.createElement("p", null,
                    React.createElement(FormattedMessage, { id: "ui-br-agency.outputs.wipo-report.result.totalCount", values: {
                            agency: agencyLabel((_d = reportData.registrationAgency) !== null && _d !== void 0 ? _d : WipoReportDataRegistrationAgencyEnum.ISBN),
                            year: reportData.year,
                            count: formatCount((_e = reportData.totalCount) !== null && _e !== void 0 ? _e : 0),
                            b: function (chunks) { return React.createElement("b", null, chunks); },
                        } })),
                React.createElement("p", null,
                    React.createElement(FormattedMessage, { id: "ui-br-agency.outputs.wipo-report.result.yearCount", values: {
                            agency: agencyLabel((_f = reportData.registrationAgency) !== null && _f !== void 0 ? _f : WipoReportDataRegistrationAgencyEnum.ISBN),
                            year: reportData.year,
                            count: formatCount((_g = reportData.yearCount) !== null && _g !== void 0 ? _g : 0),
                            b: function (chunks) { return React.createElement("b", null, chunks); },
                        } })))))));
};
